/**
 * Transfers store (engine-agnostic, app-lifetime). Jobs are keyed by id and
 * always hold the FULL snapshot the main process sent last (`transfer-list`
 * on hydration, then every `transfer-progress` event) — never a delta merge.
 * `applyEvent` reports what changed so the single subscriber
 * (`useTransferEvents`) can toast / track a terminal transition exactly once
 * even when two windows or a StrictMode remount replay the same snapshot.
 */
import { create } from 'zustand';
import type { TransferCollisionDecision, TransferItemError, TransferJob, TransferJobState } from '../../../types/azureStorageTransfers';
import { isTerminalTransferState } from '../model/transferJobRules';

/**
 * The UI surface that started a job (S2 adds row / keyboard / drop / tree), or restored at startup — the
 * `feature:storage_transfer` `surface` bucket (review E F11), never a name or path. 'mcp' is kept for callers; the
 * terminal event takes MCP from main's `job.origin` instead, and a UI job nobody noted reports 'unknown'.
 */
export type TransferJobSource = 'toolbar' | 'row' | 'keyboard' | 'drop' | 'tree' | 'mcp' | 'restored';
export type TransferPendingAction = 'resume' | 'restart' | 'cancel';
export type TransferCollisionRule = { action: TransferCollisionDecision['action']; setOn: string };
/** 'initial' keeps the store's copy of a job an event already delivered (it raced ahead of the list); 'refresh' takes the list as truth. */
export type TransferHydrateMode = 'initial' | 'refresh';
/** Last UI/UX pass (Codex Z2): the state of the last `transfer-list` read, so a failure never reads as an empty history. */
export type TransferHistoryRead = { state: 'idle' | 'loading' | 'ready' } | { state: 'failed'; message: string };

export type TransferApplyResult =
  | { kind: 'appeared'; previousState: undefined }
  | { kind: 'transition' | 'progress' | 'terminal'; previousState: TransferJobState };

export type TransfersState = {
  jobs: Record<string, TransferJob>;
  order: string[];
  hydrated: boolean;
  historyRead: TransferHistoryRead;
  sources: Record<string, TransferJobSource>;
  lastErrors: Record<string, TransferItemError>;
  throughputBps: Record<string, number>;
  /** `${code}@${attempt}` of the last notice already surfaced per job (dedupes repeated snapshots). */
  noticeKeys: Record<string, string>;
  rowNotices: Record<string, string>;
  pending: Record<string, TransferPendingAction>;
  /**
   * Review A3: "Do the same for the other conflicts" — the choice that answers this job's NEXT prompts (`setOn` = the
   * prompt the user answered, which main still shows until it publishes the next one); dropped at a terminal state.
   */
  collisionRules: Record<string, TransferCollisionRule>;
  hydrate: (jobs: TransferJob[], mode?: TransferHydrateMode) => void;
  setHistoryRead: (read: TransferHistoryRead) => void;
  applyEvent: (event: { job: TransferJob; lastError?: TransferItemError; throughputBps?: number }) => TransferApplyResult;
  noteJobSource: (jobId: string, source: TransferJobSource) => void;
  setRowNotice: (jobId: string, notice: string | undefined) => void;
  setPending: (jobId: string, action: TransferPendingAction | undefined) => void;
  setCollisionRule: (jobId: string, rule: TransferCollisionRule | undefined) => void;
  markNoticeSeen: (jobId: string, key: string) => void;
  /** INT-01: drops exactly the jobs main cleared (its `transfer-clear-finished` answer) — never a job main kept. */
  clearFinished: (clearedIds: readonly string[]) => void;
  forget: (jobId: string) => void;
  reset: () => void;
};

const omit = <T,>(record: Record<string, T>, key: string): Record<string, T> => {
  if (!(key in record)) return record;
  const { [key]: _dropped, ...rest } = record;
  return rest;
};

/** Every per-job record minus `dropped` (Clear finished's cleared ids, a forgotten job). */
function withoutJobs(state: TransfersState, dropped: ReadonlySet<string>): Partial<TransfersState> {
  const pick = <T,>(record: Record<string, T>): Record<string, T> => Object.fromEntries(Object.entries(record).filter(([id]) => !dropped.has(id)));
  return { order: state.order.filter((id) => !dropped.has(id)), jobs: pick(state.jobs), lastErrors: pick(state.lastErrors), throughputBps: pick(state.throughputBps), noticeKeys: pick(state.noticeKeys), rowNotices: pick(state.rowNotices), pending: pick(state.pending), collisionRules: pick(state.collisionRules) };
}

const EMPTY = { jobs: {}, order: [], hydrated: false, historyRead: { state: 'idle' } as TransferHistoryRead, sources: {}, lastErrors: {}, throughputBps: {}, noticeKeys: {}, rowNotices: {}, pending: {}, collisionRules: {} } satisfies Partial<TransfersState>;

export const useTransfersStore = create<TransfersState>((set, get) => ({
  ...EMPTY,

  hydrate: (snapshot, mode = 'refresh') =>
    set((state) => {
      const jobs: Record<string, TransferJob> = {};
      const sources = { ...state.sources };
      for (const job of snapshot) {
        jobs[job.id] = mode === 'initial' && state.jobs[job.id] ? state.jobs[job.id] : job;
        if (!sources[job.id]) sources[job.id] = 'restored';
      }
      return { jobs, order: snapshot.map((job) => job.id), hydrated: true, sources };
    }),

  setHistoryRead: (historyRead) => set({ historyRead }),

  applyEvent: ({ job, lastError, throughputBps }) => {
    const previous = get().jobs[job.id];
    set((state) => ({
      jobs: { ...state.jobs, [job.id]: job },
      order: previous ? state.order : [...state.order, job.id],
      lastErrors: lastError ? { ...state.lastErrors, [job.id]: lastError } : omit(state.lastErrors, job.id),
      throughputBps: throughputBps === undefined ? state.throughputBps : { ...state.throughputBps, [job.id]: throughputBps },
      rowNotices: omit(state.rowNotices, job.id),
      pending: previous && previous.state !== job.state ? omit(state.pending, job.id) : state.pending,
      collisionRules: isTerminalTransferState(job.state) ? omit(state.collisionRules, job.id) : state.collisionRules,
    }));
    if (!previous) return { kind: 'appeared', previousState: undefined };
    if (previous.state === job.state) return { kind: 'progress', previousState: previous.state };
    if (isTerminalTransferState(job.state)) return { kind: 'terminal', previousState: previous.state };
    return { kind: 'transition', previousState: previous.state };
  },

  noteJobSource: (jobId, source) => set((state) => ({ sources: { ...state.sources, [jobId]: source } })),
  setRowNotice: (jobId, notice) => set((state) => ({ rowNotices: notice === undefined ? omit(state.rowNotices, jobId) : { ...state.rowNotices, [jobId]: notice } })),
  setPending: (jobId, action) => set((state) => ({ pending: action === undefined ? omit(state.pending, jobId) : { ...state.pending, [jobId]: action } })),
  setCollisionRule: (jobId, rule) => set((state) => ({ collisionRules: rule === undefined ? omit(state.collisionRules, jobId) : { ...state.collisionRules, [jobId]: rule } })),
  markNoticeSeen: (jobId, key) => set((state) => ({ noticeKeys: { ...state.noticeKeys, [jobId]: key } })),

  clearFinished: (clearedIds) => set((state) => withoutJobs(state, new Set(clearedIds))),

  // S7 review D F4: main dropped the job (Forget); the row goes with every per-job entry.
  forget: (jobId) => set((state) => withoutJobs(state, new Set([jobId]))),

  reset: () => set({ ...EMPTY }),
}));

export function selectOrderedJobs(state: Pick<TransfersState, 'jobs' | 'order'>): TransferJob[] {
  return state.order.map((id) => state.jobs[id]).filter((job): job is TransferJob => Boolean(job));
}
